import React, { FC, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from "@mui/material";
import { useAppSelector, useAppDispatch } from "../../../store";
import { useMatchStats } from "../hooks/useMatchStats";
import {
  getTournoi,
  getBoards,
  fetchBoards,
  getRounds,
  fetchRounds,
} from "../../../features/tournoi/tournoiSlice";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import firestoreInstance from "../../../services/firestoreInstance";
import { Matchs } from "../../../types";
import TableauProgressBlock from "./TableauProgressBlock";
import { fetchParticipationsWithPlayers } from "../../../helpers/fetchParticipationsWithPlayer";
import { calendarDayKey } from "../../player/playerHelper";
import { Participation } from "../../../types/Participation";
import { formatDuration } from "../../../utils/dateUtils";
import { currentDate } from "../../../helpers/currentDate";

const StatCard: FC<{ title: string; value: number; color?: string }> = ({
  title,
  value,
  color,
}) => (
  <Card>
    <CardContent>
      <Typography color="textSecondary" gutterBottom>
        {title}
      </Typography>
      <Typography variant="h4" component="div" color={color}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const StatsPage: FC = () => {
  const dispatch = useAppDispatch();
  const tournoi = useAppSelector(getTournoi);
  const boards = useAppSelector(getBoards);
  const rounds = useAppSelector(getRounds);
  const [matches, setMatches] = React.useState<Matchs[]>([]);
  const [loading, setLoading] = React.useState(true);
  const stats = useMatchStats(matches);
  const [participations, setParticipations] = React.useState<Participation[]>(
    []
  );
  const [tab, setTab] = React.useState(0);
  const [selectedDate, setSelectedDate] = React.useState<string>(
    currentDate().toISOString().split("T")[0]
  );

  useEffect(() => {
    if (!tournoi?.id) return;
    dispatch(fetchBoards());
    dispatch(fetchRounds());
  }, [dispatch, tournoi?.id]);

  useEffect(() => {
    if (!tournoi?.id) return;

    const collectionName = tournoi.statsCollectionName || "matchs";
    const matchsCollectionRef =
      collectionName === "matchs"
        ? collection(firestoreInstance, "tournois", tournoi.id, "matchs")
        : collection(firestoreInstance, collectionName);
    const q = query(matchsCollectionRef, orderBy("dateCreation"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const matchs = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
          dateCreation: docSnap.data().dateCreation?.toDate(),
          dateLancement: docSnap.data().dateLancement?.toDate(),
          dateFin: docSnap.data().dateFin?.toDate(),
        })) as Matchs[];
        setMatches(matchs);
        setLoading(false);
      },
      (error) => {
        console.error("Stats: écoute matchs impossible", error);
        setMatches([]);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [tournoi?.id, tournoi?.statsCollectionName]);

  useEffect(() => {
    if (!tournoi?.id) return;
    let cancelled = false;
    fetchParticipationsWithPlayers(tournoi.id)
      .then((rows) => {
        if (!cancelled) setParticipations(rows);
      })
      .catch((error: unknown) => {
        console.error("Stats: chargement participations impossible", error);
        if (!cancelled) setParticipations([]);
      });
    return () => {
      cancelled = true;
    };
  }, [tournoi?.id]);

  const getBoardInfo = (tableauId: string) => {
    const board = boards.find((b) => b.id === tableauId);
    return {
      name: board?.name || "Tableau inconnu",
      label: board?.label || "",
    };
  };

  // Grouper les participations par tableauId
  const participationsByTableau: Record<string, Participation[]> =
    React.useMemo(() => {
      const map: Record<string, Participation[]> = {};
      for (const p of participations) {
        const tid = p.tableauId || p.tableau?.id;
        if (!tid) continue;
        const key = String(tid);
        if (!map[key]) map[key] = [];
        map[key].push(p);
      }
      return map;
    }, [participations]);

  // Filtrer et trier les tableaux (jour civil local, comme occupation tables)
  const filteredAndSortedBoards = React.useMemo(() => {
    return boards
      .filter((board) => {
        if (!board.date) return false;
        return calendarDayKey(board.date) === selectedDate;
      })
      .sort((a, b) => {
        // D'abord trier par heure limite
        const timeA = a.heureLimite || "";
        const timeB = b.heureLimite || "";
        if (timeA !== timeB) {
          return timeA.localeCompare(timeB);
        }
        // Ensuite trier par nom
        return (a.name || "").localeCompare(b.name || "");
      });
  }, [boards, selectedDate]);

  // Récupérer toutes les dates uniques des tableaux
  const availableDates = React.useMemo(() => {
    const dates = new Set<string>();
    boards.forEach((board) => {
      if (!board.date) return;
      dates.add(calendarDayKey(board.date));
    });
    return Array.from(dates).sort();
  }, [boards]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (boards.length === 0) {
    return (
      <Box p={3}>
        <Typography color="text.secondary">
          Aucun tableau disponible pour ce tournoi.
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Avancement des tableaux" />
        <Tab label="Statistiques globales" />
        <Tab label="Par tableau" />
        <Tab label="Par nombre de joueurs" />
      </Tabs>

      {tab === 0 && (
        <>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: -0.3 }}>
                Avancement des tableaux
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Progression compétitive par tableau — distinct de l’occupation
                des tables
              </Typography>
            </Box>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>Journée</InputLabel>
              <Select
                value={
                  availableDates.includes(selectedDate)
                    ? selectedDate
                    : availableDates[0] || ""
                }
                label="Journée"
                onChange={(e) => setSelectedDate(e.target.value)}
                disabled={availableDates.length === 0}
              >
                {availableDates.map((date) => (
                  <MenuItem key={date} value={date}>
                    {new Date(`${date}T12:00:00`).toLocaleDateString("fr-FR", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          {filteredAndSortedBoards.length === 0 ? (
            <Typography color="text.secondary">
              Aucun tableau pour cette journée.
            </Typography>
          ) : (
            <Grid container spacing={2.5}>
              {filteredAndSortedBoards.map((board) => (
                <Grid item xs={12} md={6} lg={4} key={board.id}>
                  <TableauProgressBlock
                    tableau={board}
                    participations={
                      participationsByTableau[String(board.id)] || []
                    }
                    matchs={matches.filter(
                      (m) => String(m.tableauId) === String(board.id)
                    )}
                    tours={rounds}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {tab === 1 && (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Matchs lançables"
                value={stats.lancables}
                color="info.main"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Matchs bloqués"
                value={stats.bloques}
                color="warning.main"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Matchs en cours"
                value={stats.enCours}
                color="primary"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Matchs terminés"
                value={stats.termines}
                color="success.main"
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Temps entre étapes (tous tableaux)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography color="textSecondary">
                  Médiane création → lancement
                </Typography>
                <Typography variant="h6">
                  {formatDuration(stats.medianCreationToLaunch)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography color="textSecondary">
                  Médiane lancement → fin
                </Typography>
                <Typography variant="h6">
                  {formatDuration(stats.medianLaunchToEnd)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography color="textSecondary">
                  95% création → lancement
                </Typography>
                <Typography variant="h6">
                  {formatDuration(stats.p95CreationToLaunch)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography color="textSecondary">
                  95% lancement → fin
                </Typography>
                <Typography variant="h6">
                  {formatDuration(stats.p95LaunchToEnd)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </>
      )}

      {tab === 2 && (
        <>
          <Grid container spacing={3}>
            {Object.entries(stats.parTableau).map(
              ([tableauId, tableauStats]) => {
                const boardInfo = getBoardInfo(tableauId);
                return (
                  <Grid item xs={12} sm={6} md={4} key={tableauId}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Tableau {boardInfo.name} - {boardInfo.label}
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={6} md={6}>
                            <Typography color="textSecondary">
                              Lançables
                            </Typography>
                            <Typography variant="h6" color="info.main">
                              {tableauStats.lancables}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={6} md={6}>
                            <Typography color="textSecondary">
                              Bloqués
                            </Typography>
                            <Typography variant="h6" color="warning.main">
                              {tableauStats.bloques}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={6} md={6}>
                            <Typography color="textSecondary">
                              En cours
                            </Typography>
                            <Typography variant="h6" color="primary">
                              {tableauStats.enCours}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={6} md={6}>
                            <Typography color="textSecondary">
                              Terminés
                            </Typography>
                            <Typography variant="h6" color="success.main">
                              {tableauStats.termines}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Temps entre étapes
                          </Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography color="textSecondary" fontSize={13}>
                                Médiane créa → lancement
                              </Typography>
                              <Typography variant="body2">
                                {formatDuration(
                                  tableauStats.medianCreationToLaunch
                                )}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography color="textSecondary" fontSize={13}>
                                Médiane lancement → fin
                              </Typography>
                              <Typography variant="body2">
                                {formatDuration(tableauStats.medianLaunchToEnd)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography color="textSecondary" fontSize={13}>
                                95% créa → lancement
                              </Typography>
                              <Typography variant="body2">
                                {formatDuration(
                                  tableauStats.p95CreationToLaunch
                                )}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography color="textSecondary" fontSize={13}>
                                95% lancement → fin
                              </Typography>
                              <Typography variant="body2">
                                {formatDuration(tableauStats.p95LaunchToEnd)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              }
            )}
          </Grid>
        </>
      )}

      {tab === 3 && (
        <>
          <Box sx={{ overflowX: "auto" }}>
            <table
              style={{
                minWidth: 500,
                width: "100%",
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: 8 }}>Nb joueurs</th>
                  <th style={{ textAlign: "right", padding: 8 }}>Matchs</th>
                  <th style={{ textAlign: "right", padding: 8 }}>
                    Médiane créa → lancement
                  </th>
                  <th style={{ textAlign: "right", padding: 8 }}>
                    Médiane lancement → fin
                  </th>
                  <th style={{ textAlign: "right", padding: 8 }}>
                    95% créa → lancement
                  </th>
                  <th style={{ textAlign: "right", padding: 8 }}>
                    95% lancement → fin
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(stats.parNbJoueurs)
                  .sort((a, b) => Number(a[0]) - Number(b[0]))
                  .map(([nb, s]) => (
                    <tr key={nb}>
                      <td style={{ padding: 8 }}>{nb}</td>
                      <td style={{ textAlign: "right", padding: 8 }}>
                        {s.count}
                      </td>
                      <td style={{ textAlign: "right", padding: 8 }}>
                        {formatDuration(s.medianCreationToLaunch)}
                      </td>
                      <td style={{ textAlign: "right", padding: 8 }}>
                        {formatDuration(s.medianLaunchToEnd)}
                      </td>
                      <td style={{ textAlign: "right", padding: 8 }}>
                        {formatDuration(s.p95CreationToLaunch)}
                      </td>
                      <td style={{ textAlign: "right", padding: 8 }}>
                        {formatDuration(s.p95LaunchToEnd)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </Box>
        </>
      )}
    </Box>
  );
};

export default StatsPage;
